import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { ShoppingCartOutlined } from "@material-ui/icons";
import { Color } from "@material-ui/lab/Alert";
import { useUser } from "@saleor/auth";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import Skeleton from "@saleor/components/Skeleton";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import ContractNoCustomer from "@saleor/createOrder/components/ContractNo/ContractNoCustomer";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import ModalProductDetail from "@saleor/createOrder/components/ModalProductDetail";
import {
  ContractQuery,
  ScgpCustomerContractQuery,
  useCartTotalCheckoutQuery,
  useCreateContractCheckoutMutation,
  useCreateCustomerCartMutation,
  useCustomerCartLazyQuery,
  useCustomerCartProductVariantLazyQuery,
  useCustomerCartsQuery,
  useCustomerCartTotalsQuery,
  useGetCartProductItemsQuery,
  useGetCreditLimitQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { RelayToFlatNoEdge } from "@saleor/types";
import {
  getSalesUnit,
  PROD_CATEG_1,
  PROD_CATEG_2,
  SALE_CATEG_1
} from "@saleor/utils/constants";
import { convertToRol } from "@saleor/utils/data/convertUnit";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ContractNo from "../ContractNo";

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%"
    },
    rootCustomer: {
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: "4px"
    },
    contract: {
      "& .MuiTextField-root": {
        "& .MuiOutlinedInput-input": {
          paddingTop: "0.7rem",
          paddingBottom: "0.8rem",
          fontSize: "1.4rem",
          lineHeight: "22px",
          color: theme.palette.grey[900]
        },
        '& [class*="SingleAutocompleteSelectField-adornment"]': {
          color: theme.palette.grey[900],
          position: "absolute",
          right: "6px",
          top: "5px"
        }
      }
    },
    header: {
      "& .MuiTableCell-root": {
        fontWeight: 700
      }
    },
    colId: {
      width: 120
    },
    colDataName: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      "& button": {
        height: "3.2rem",
        minWidth: "180px"
      }
    },
    colQuantity: {
      width: 120,
      minWidth: 120,
      maxWidth: 120
    },
    colUnit: {
      width: 80,
      minWidth: 80,
      maxWidth: 80
    },
    colButton: {
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      "& button": {
        height: "3.2rem",
        minWidth: "136px"
      }
    },
    cellWithBackground: {
      background: `${theme.palette.grey[200]} !important`,
      paddingLeft: "8px",
      textAlign: "center",
      width: 150,
      "& > div": {
        justifyContent: "center"
      }
    },
    cellWithBackgroundRight: {
      background: `${theme.palette.grey[200]} !important`,
      paddingLeft: "8px",
      textAlign: "right",
      width: 120,
      "& > div": {
        justifyContent: "flex-end"
      }
    },
    table: {
      borderCollapse: "collapse"
    },
    tableRow: {
      borderTop: "2px solid white"
    },
    cellIndex: {
      fontSize: "1.4rem",
      color: theme.palette.grey[900]
    },
    title: {
      marginTop: "2.4rem",
      marginBottom: "1.6rem",
      color: theme.palette.grey[900]
    },
    btnOpen: {
      fontSize: "1.4rem"
    },
    lightSpan: {
      fontWeight: 100
    },
    snackHeadTitle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "1.2rem"
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    },
    errorText: {
      marginTop: 5,
      color: theme.palette.error.light
    }
  }),
  { name: "ViewContractPage" }
);

export interface ViewContractPageProps {
  data: RelayToFlatNoEdge<ContractQuery["contract"]>;
  dataCustomer: RelayToFlatNoEdge<
    ScgpCustomerContractQuery["scgpCustomerContract"]
  >;
}

const ViewContractPage: React.FC<ViewContractPageProps> = ({
  dataCustomer,
  data
}) => {
  const isStaff = isStaffRole();
  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );
  const [openModalProduct, setOpenModalProduct] = useState<boolean>(false);
  const [modalProductId, setModalProductId] = useState<string>("");
  const [modalKey, setModalKey] = useState<number>(0);
  const [contractMaterialIdModal, setContractMaterialIdModal] = useState<
    string
  >("");
  const [dataChangeInput, setDataChangeInput] = useState([]);
  const [dataChangeInputCustomer, setDataChangeInputCustomer] = useState([]);
  const [typeAlert, setTypeAlert] = useState<Color>("success");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [_componentKey, setComponentKey] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const classes = useStyles();
  const intl = useIntl();
  const { user, soldToId } = useUser();
  const [, dispatchAppState] = useAppState();

  const [customerCarts, setCustomerCarts] = useState<any>();

  const forceReRender = () => {
    setComponentKey(prev => prev + 1);
  };

  const { data: dataCreditLimit } = useGetCreditLimitQuery({
    variables: {
      input: {
        contractNo: data?.contractNo,
        salesOrgCode: data?.salesOrganization?.code,
        soldToCode: data?.customer?.soldToCode
      }
    },
    fetchPolicy: "no-cache",
    skip: !data?.contractNo
  });

  const {
    data: customerCartsData,
    refetch: customerCartsRefetch
  } = useCustomerCartsQuery({
    variables: {
      first: 100,
      soldToId
    }
  });

  const [
    customerCartLazy,
    { data: customerCartLazyData }
  ] = useCustomerCartLazyQuery();
  const [
    customerCartProductVariant,
    { data: customerCartProductVariantLazyData }
  ] = useCustomerCartProductVariantLazyQuery();

  const [remainInCart, setRemainInCart] = useState<number>(0);
  // const [remainInCartCateg2, setRemainInCartCateg2] = useState<number>(0);

  const {
    data: dataProductInCart,
    refetch: fetchGetProductInCart
  } = useGetCartProductItemsQuery({
    variables: {
      contractCode: data?.contractNo,
      soldToCode: data?.customer?.soldToCode
    },
    fetchPolicy: "no-cache",
    skip: !data?.contractNo
  });

  const handleAddProduct = () => {
    if (dataCreditLimit?.getCreditLimit?.creditBlockStatus) {
      toggleCloseCreateOrderError();
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!modalProductId) {
      return;
    }
    let product;
    if (user.isStaff) {
      product = data?.products?.find(
        item => item?.productId === modalProductId
      );
    } else {
      product = dataCustomer?.products?.find(
        item => item?.product?.id === modalProductId
      );
    }
    if (product) {
      const remainInCart = getRemainInCartByProduct(product);
      setRemainInCart(remainInCart);
      // const remainInCartCateg2 = getRemainInCartByProductCateg2(product);
      // setRemainInCartCateg2(remainInCartCateg2);
    }
  }, [dataProductInCart?.cartProductItems, modalProductId]);

  useEffect(() => {
    if (data) {
      const listProductDomestic =
        data?.products.map(item => ({ ...item, quantity: "" })) || [];
      setDataChangeInput(listProductDomestic);
    }
    if (dataCustomer) {
      const listProductCustomer =
        dataCustomer?.products.map(item => ({ ...item, quantity: "" })) || [];
      setDataChangeInputCustomer(listProductCustomer);
    }
  }, [data, dataCustomer]);

  const [addToCartDomestic] = useCreateContractCheckoutMutation();

  const [updateCartCustomer] = useCreateCustomerCartMutation();

  const { refetch: refetchCartCustomer } = useCustomerCartTotalsQuery({
    variables: {
      soldToId
    },
    skip: true
  });

  const { refetch: refetchCart } = useCartTotalCheckoutQuery({
    skip: true
  });

  const reCountCartCustomer = response => {
    if (response?.data?.customerCartTotals?.totalProducts) {
      const orderCount = response?.data?.customerCartTotals?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  };

  const reCountCartDomestic = response => {
    if (response?.data?.totalContractCheckouts?.totalProducts) {
      const orderCount =
        response?.data?.totalContractCheckouts?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  };

  const getRemainInCartByProduct = product =>
    dataProductInCart?.cartProductItems
      ?.filter(item => item?.contractProduct?.id === product?.id)
      .reduce(
        (total, currentValue) =>
          currentValue.quantity * Number(currentValue?.variant?.weight) + total,
        0
      );

  const getRemainInCartByProductCateg2 = product =>
    dataProductInCart?.cartProductItems
      ?.filter(item => item?.contractProduct?.id === product?.id)
      .reduce((total, currentValue) => currentValue.quantity + total, 0);

  const isCreateOrderFlow =
    window.location.href.includes("create-order") ||
    window.location.href.includes("sold-to");

  const onOpenModalProduct = product => {
    const productId = user.isStaff ? product.productId : product.product.id;
    const remainInCart = getRemainInCartByProduct(product);
    setRemainInCart(remainInCart);
    // const remainInCartCateg2 = getRemainInCartByProductCateg2(product);
    // setRemainInCartCateg2(remainInCartCateg2);
    setModalProductId(productId);
    setContractMaterialIdModal(product.id);
    setModalKey(prevKey => prevKey + 1);
    setOpenModalProduct(true);
    // console.log("Mat84 - Create Order");
  };

  const onCloseModalProduct = () => {
    setOpenModalProduct(false);
  };

  const isMatType8182 = product =>
    product.materialType === "81" || product.materialType === "82";

  const haveRowMatType8182 = () => {
    const { isStaff } = user;
    if (isStaff) {
      return data?.products.some(product => isMatType8182(product.product));
    } else {
      return dataCustomer?.products.some(product =>
        isMatType8182(product.product)
      );
    }
  };

  const addToCart = product => {
    const isError = getQuantityError(product);
    if (isError) {
      setTypeAlert("error");
      setOpenAlert(true);
      return;
    }
    const { isStaff } = user;
    if (isStaff) {
      if (dataCreditLimit?.getCreditLimit?.creditBlockStatus) {
        toggleCloseCreateOrderError();
        return;
      }
      const dataAdd = dataChangeInput.find(item => item.id === product.id);
      if (dataAdd.quantity) {
        addToCartDomestic({
          variables: {
            input: {
              contractId: data?.id,
              userId: data?.customer?.id,
              lines: [
                {
                  quantity: dataAdd.quantity,
                  productId: dataAdd.productId,
                  contractMaterialId: dataAdd.id
                }
              ]
            }
          }
        }).then(response => {
          if (response?.data?.createContractCheckout?.checkout?.id) {
            setTypeAlert("success");
            setOpenAlert(true);
            fetchGetProductInCart();
            refetchCart().then(response => {
              reCountCartDomestic(response);
              const newDataChange = dataChangeInput.map(item => {
                if (item?.id === product?.id) {
                  item.quantity = "";
                }
                return item;
              });
              setDataChangeInput(newDataChange);
            });
          } else {
            setTypeAlert("error");
            setOpenAlert(true);
          }
        });
      } else {
        setTypeAlert("error");
        setOpenAlert(true);
      }
    } else {
      const dataAdd = dataChangeInputCustomer.find(
        item => item.id === product.id
      );
      if (dataAdd.quantity) {
        updateCartCustomer({
          variables: {
            input: {
              soldToId,
              customerContractId: dataCustomer?.id,
              customerId: dataCustomer?.customer.id,
              lines: [
                {
                  quantity: dataAdd.quantity,
                  productId: dataAdd.product.id,
                  contractMaterialId: dataAdd.id
                }
              ]
            }
          }
        }).then(response => {
          if (response?.data?.createCustomerCart?.cart?.id) {
            setTypeAlert("success");
            setOpenAlert(true);
            handleChangeQuantity("", product);
            refetchCartCustomer().then(response =>
              reCountCartCustomer(response)
            );
            customerCartsRefetch();
          } else {
            setTypeAlert("error");
            setOpenAlert(true);
          }
        });
      } else {
        setTypeAlert("error");
        setOpenAlert(true);
      }
    }
  };

  const handleChangeQuantity = (newQuantity, product) => {
    const { isStaff } = user;
    const isProductCateg2 = PROD_CATEG_2(data?.prcGroup1);
    if (isStaff) {
      const newDateChangeInput = [...dataChangeInput];
      const dataChange = newDateChangeInput.find(
        item => item?.id === product?.id
      );
      const remainCart = getRemainInCartByProduct(product);
      const remainCartCateg2 = getRemainInCartByProductCateg2(product);
      const limitQuantity = dataChange?.remain;
      dataChange.quantity = newQuantity;
      /* Converting ROL to TON */
      dataChange.errorQuantity =
        parseFloat(newQuantity) * product.product.weight >
        limitQuantity - remainCart;
      /* Converting ROL to TON */
      if (isProductCateg2) {
        const newQtys = Number(newQuantity);
        const remainCalc = limitQuantity - remainCartCateg2;
        const remainCalcRound = Number(remainCalc.toFixed(3));
        dataChange.errorQuantity = newQtys > remainCalcRound;
      }
      setDataChangeInput(newDateChangeInput);
    } else {
      const newDataChangeInputCustomer = [...dataChangeInputCustomer];
      const dataChange = newDataChangeInputCustomer.find(
        item => item?.id === product?.id
      );
      const customerCartChange = customerCarts?.customerCart?.cartItems.find(
        item => item?.contractProduct?.id === product?.id
      );
      const limitQuantity = dataChange.limitQuantity;

      if (customerCartChange) {
        dataChange.errorQuantity =
          parseFloat(newQuantity) + customerCartChange.quantity > limitQuantity;
      } else {
        dataChange.errorQuantity = parseFloat(newQuantity) > limitQuantity;
      }

      dataChange.quantity = newQuantity;
      setDataChangeInputCustomer(newDataChangeInputCustomer);
    }
    forceReRender();
  };

  const getQuantityInput = product => {
    const { isStaff } = user;
    if (isStaff) {
      return (
        dataChangeInput.find(item => product.id === item.id)?.quantity || ""
      );
    } else {
      return (
        dataChangeInputCustomer.find(item => product.id === item.id)
          ?.quantity || ""
      );
    }
  };

  const getQuantityError = product => {
    const { isStaff } = user;
    if (isStaff) {
      return (
        dataChangeInput.find(item => product.id === item.id)?.errorQuantity ||
        false
      );
    } else {
      return (
        dataChangeInputCustomer.find(item => product.id === item.id)
          ?.errorQuantity || false
      );
    }
  };

  const renderTextErrorQuantity = product => {
    const divErrorText = (
      <div className={classes.errorText}>
        {intl.formatMessage({
          defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
        })}
      </div>
    );
    return getQuantityError(product) ? divErrorText : "";
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const toggleCloseCreateOrderError = () => {
    setOpenCreateOrderError(val => !val);
  };

  const renderHeaderDomestic = (
    <TableRow className={classes.header}>
      <TableCellHeader className={classes.colId}>
        <FormattedMessage defaultMessage="ลำดับ" />
      </TableCellHeader>
      <TableCellHeader>
        <FormattedMessage defaultMessage="Material Code" />
      </TableCellHeader>
      <TableCellHeader>
        <FormattedMessage defaultMessage="ช่ือสินค้า" />
      </TableCellHeader>
      <TableCellHeader className={classes.colQuantity}>
        {haveRowMatType8182() ? (
          <FormattedMessage defaultMessage="จำนวนสั่ง" />
        ) : (
          ""
        )}
      </TableCellHeader>
      <TableCellHeader className={classes.colUnit}>
        {haveRowMatType8182() ? (
          <FormattedMessage defaultMessage="หน่วย" />
        ) : (
          ""
        )}
      </TableCellHeader>
      <TableCellHeader className={classes.colButton} textAlign={"center"} />
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="จำนวน" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="คงเหลือ" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackground}>
        <FormattedMessage defaultMessage="หน่วย" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="ราคา" />
        <span className={classes.lightSpan}>({data?.currency})</span>
        <br />
        <FormattedMessage defaultMessage="ต่อหน่วยขาย" />
      </TableCellHeader>
    </TableRow>
  );

  const renderHeaderCustomer = (
    <TableRow className={classes.header}>
      <TableCellHeader className={classes.colId}>
        <FormattedMessage defaultMessage="ลำดับ" />
      </TableCellHeader>
      <TableCellHeader>
        <FormattedMessage defaultMessage="ช่ือสินค้า" />
      </TableCellHeader>
      <TableCellHeader className={classes.colQuantity}>
        {haveRowMatType8182() ? (
          <FormattedMessage defaultMessage="จำนวนสั่ง" />
        ) : (
          ""
        )}
      </TableCellHeader>
      <TableCellHeader className={classes.colUnit}>
        {haveRowMatType8182() ? (
          <FormattedMessage defaultMessage="หน่วย" />
        ) : (
          ""
        )}
      </TableCellHeader>
      <TableCellHeader className={classes.colButton} textAlign={"center"} />
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="จำนวน" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="คงเหลือ" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackground}>
        <FormattedMessage defaultMessage="หน่วย" />
      </TableCellHeader>
      <TableCellHeader className={classes.cellWithBackgroundRight}>
        <FormattedMessage defaultMessage="ราคา" />
        <span className={classes.lightSpan}>({dataCustomer?.currency})</span>
        <br />
        <FormattedMessage defaultMessage="ต่อหน่วย" />
      </TableCellHeader>
    </TableRow>
  );

  useEffect(() => {
    if (!data?.products) {
      return;
    } else {
      for (const product of data.products) {
        const quantity = getQuantityInput(product);
        if (quantity) {
          handleChangeQuantity(quantity, product);
        }
      }
    }
  }, [dataProductInCart, data?.products]);

  useEffect(() => {
    if (customerCartsData) {
      const totalContract = customerCartsData.customerCarts.edges;
      if (totalContract.length > 0) {
        const filteredContract = totalContract.filter(
          contract => contract?.node?.contract?.code === dataCustomer?.code
        );

        if (filteredContract.length > 0) {
          customerCartLazy({
            variables: {
              cartId: filteredContract[0].node.id
            }
          });
          customerCartProductVariant({
            variables: {
              id: filteredContract[0].node.id
            }
          });
        }
      }
    }
  }, [customerCartsData, dataCustomer]);

  useEffect(() => {
    if (customerCartLazyData && customerCartProductVariantLazyData) {
      const {
        customerCart: { cartItems, ...rest }
      } = customerCartLazyData;
      const {
        customerCartProductVariant: { contractProduct }
      } = customerCartProductVariantLazyData;
      const newCartItems = cartItems.map(cartItem => ({
        ...cartItem,
        contractProduct: contractProduct.find(
          contract => contract.id === cartItem.contractProductId
        )
      }));
      setCustomerCarts({ customerCart: { ...rest, cartItems: newCartItems } });
    }
  }, [customerCartLazyData, customerCartProductVariantLazyData]);

  return (
    <>
      <SnackbarAlert
        severity={typeAlert}
        open={openAlert}
        onClose={handleCloseAlert}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <div className={classes.snackHeadTitle}>
          <MyTypography
            variant="subtitle2"
            style={{ color: "#ffffff", marginRight: "2px" }}
          >
            {intl.formatMessage({
              defaultMessage: "Added to cart",
              description: "dialog header"
            })}
          </MyTypography>
          <MyTypography variant="body3" style={{ color: "#ffffff" }}>
            {typeAlert === "success" ? "เพิ่มสินค้าลงตะกร้าสำเร็จ" : ""}
            {typeAlert === "error" ? "เพิ่มสินค้าลงตะกร้าไม่สำเร็จ" : ""}
          </MyTypography>
        </div>
      </SnackbarAlert>
      {user.isStaff ? (
        <ContractNo data={data} />
      ) : (
        <ContractNoCustomer data={dataCustomer} />
      )}
      <MyTypography variant="h7" className={classes.title}>
        {intl.formatMessage({ defaultMessage: "รายการสินค้า" })}{" "}
        {!user.isStaff &&
          dataCustomer?.products?.length > 0 &&
          `(${dataCustomer?.products?.length})`}
      </MyTypography>
      <div
        className={classNames(classes.root, {
          [classes.rootCustomer]: !user.isStaff
        })}
        ref={containerRef}
      >
        <Table className={classes.table}>
          <TableHead>
            {user.isStaff ? renderHeaderDomestic : renderHeaderCustomer}
          </TableHead>
          <TableBody>
            {data &&
              user.isStaff &&
              renderCollection(data?.products, (product, index) => (
                <TableRow
                  className={classes.tableRow}
                  key={product ? product.id : ""}
                >
                  <TableCell className={classes.cellIndex}>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    {product ? (
                      <MyTypography variant="subtitle3">
                        {product?.materialCode}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colDataName}>
                    {product ? (
                      <MyTypography variant="subtitle3">
                        {product.materialDescription}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {isMatType8182(product.product) ? (
                      <>
                        <NumberFormat
                          id={`dataProducts[${index}].quantity`}
                          onChange={e =>
                            handleChangeQuantity(e.target.value, product)
                          }
                          value={getQuantityInput(product)}
                          allowLeadingZeros={
                            !SALE_CATEG_1(
                              getSalesUnit(
                                product?.salesUnit,
                                product?.matGroup1
                              )
                            )
                          }
                          decimalScale={
                            SALE_CATEG_1(
                              getSalesUnit(
                                product?.salesUnit,
                                product?.matGroup1
                              )
                            )
                              ? 3
                              : 0
                          }
                          error={getQuantityError(product)}
                          placeholder={
                            SALE_CATEG_1(
                              getSalesUnit(
                                product?.salesUnit,
                                product?.matGroup1
                              )
                            )
                              ? "0.000"
                              : "0"
                          }
                          rightAlignPlaceholder={SALE_CATEG_1(
                            getSalesUnit(product?.salesUnit, product?.matGroup1)
                          )}
                          fixedDecimalScale={SALE_CATEG_1(
                            getSalesUnit(product?.salesUnit, product?.matGroup1)
                          )}
                        />
                        {renderTextErrorQuantity(product)}
                      </>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {isMatType8182(product.product)
                      ? (() => {
                          if (PROD_CATEG_1(product.matGroup1)) {
                            return <FormattedMessage defaultMessage="ม้วน" />;
                          } else {
                            return <span>{product.salesUnit}</span>;
                          }
                        })()
                      : ""}
                  </TableCell>
                  <TableCell className={classes.colButton}>
                    {isMatType8182(product.product) ? (
                      <Button
                        id={`dataProducts[${index}].addToCartButton`}
                        data-testid={`dataProducts[${index}].addToCartButton`}
                        className={classes.btnOpen}
                        variant="contained"
                        color="secondary"
                        startIcon={<ShoppingCartOutlined />}
                        onClick={() => addToCart(product)}
                      >
                        {intl.formatMessage({ defaultMessage: "Add to cart" })}
                      </Button>
                    ) : (
                      <Button
                        id={`dataProducts[${index}].selectProductButton`}
                        data-testid={`dataProducts[${index}].selectProductButton`}
                        className={classes.btnOpen}
                        variant="contained"
                        color="secondary"
                        onClick={() => onOpenModalProduct(product)}
                      >
                        {intl.formatMessage({ defaultMessage: "เลือกสินค้า" })}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat
                          value={product.total}
                          isGrayDecimal
                          countDecimal={3}
                        />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat
                          value={product.remain}
                          isGrayDecimal
                          countDecimal={3}
                        />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackground}>
                    {product ? (
                      <MyTypography variant="body3">
                        {convertToRol(product.weightUnit)}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat value={product.price} />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {dataCustomer &&
              !user.isStaff &&
              renderCollection(dataCustomer?.products, (product, index) => (
                <TableRow
                  className={classes.tableRow}
                  key={product ? product.product?.name : "skeleton"}
                >
                  <TableCell className={classes.cellIndex}>
                    {index + 1}
                  </TableCell>
                  <TableCell className={classes.colDataName}>
                    {product ? (
                      <MyTypography variant="subtitle3">
                        {product?.materialDescription}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {isMatType8182(product.product) ? (
                      <>
                        <NumberFormat
                          id={`dataCustomerProducts[${index}].quantity`}
                          allowLeadingZeros
                          decimalScale={0}
                          onChange={e =>
                            handleChangeQuantity(e.target.value, product)
                          }
                          value={getQuantityInput(product)}
                          error={getQuantityError(product)}
                        />
                        {renderTextErrorQuantity(product)}
                      </>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {isMatType8182(product.product) ? (
                      <FormattedMessage defaultMessage="ม้วน" />
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell className={classes.colButton}>
                    {isMatType8182(product.product) ? (
                      <Button
                        id={`dataCustomerProducts[${index}].addToCartButton`}
                        data-testid={`dataCustomerProducts[${index}].addToCartButton`}
                        className={classes.btnOpen}
                        variant="contained"
                        color="secondary"
                        startIcon={<ShoppingCartOutlined />}
                        onClick={() => addToCart(product)}
                      >
                        {intl.formatMessage({ defaultMessage: "Add to cart" })}
                      </Button>
                    ) : (
                      <Button
                        id={`dataCustomerProducts[${index}].selectProductButton`}
                        data-testid={`dataCustomerProducts[${index}].selectProductButton`}
                        className={classes.btnOpen}
                        variant="contained"
                        color="secondary"
                        onClick={() => onOpenModalProduct(product)}
                      >
                        {intl.formatMessage({ defaultMessage: "เลือกสินค้า" })}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat
                          value={product.totalQuantity}
                          isGrayDecimal
                          countDecimal={3}
                        />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat
                          value={product.remainingQuantity}
                          isGrayDecimal
                          countDecimal={3}
                        />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackground}>
                    {product ? (
                      <MyTypography variant="body3">
                        {convertToRol(product.weightUnit)}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.cellWithBackgroundRight}>
                    {product ? (
                      <MyTypography variant="body3">
                        <DecimalFormat value={product.pricePerUnit} />
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {dataCustomer?.products || data?.products ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
      <ModalProductDetail
        isCreateOrderFlow={isCreateOrderFlow}
        // isChangeOrderFlow={false}
        customerCart={customerCarts}
        cartData={customerCarts?.customerCart?.cartItems}
        remainInCart={remainInCart}
        // remainInCartCateg2={remainInCartCateg2}
        contractMaterialId={contractMaterialIdModal}
        onClose={onCloseModalProduct}
        open={openModalProduct}
        userId={user.isStaff ? data?.customer?.id : dataCustomer?.customer?.id}
        key={modalKey}
        contractId={user.isStaff ? data?.id : dataCustomer?.id}
        productId={modalProductId}
        onAddProduct={handleAddProduct}
        onAddProductComplete={() => fetchGetProductInCart()}
      />
      <DialogCreateOrderError
        onConfirm={toggleCloseCreateOrderError}
        open={openCreateOrderError}
        okText="ปิดหน้าต่าง"
        title={
          isStaff ? "ลูกค้ารายนี้ติด Credit Block" : "ไม่สามารถดำเนินการต่อได้"
        }
        content={
          isStaff ? (
            <MyTypography variant="body2" className={classes.inlineBlock}>
              เนื่องจากลูกค้ารายนี้ติด Credit Block กรุณาติดต่อฝ่ายสินเชื่อ
            </MyTypography>
          ) : (
            <span>
              <MyTypography variant="body2" className={classes.inlineBlock}>
                เนื่องจาก Customer Credit :
              </MyTypography>{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                Credit Blocked
              </MyTypography>
            </span>
          )
        }
      />
    </>
  );
};

export default React.memo(ViewContractPage);
